import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { showToast } from '@klpkonsern/ui';
import { Block, KlpModal } from '@klp/design';
import {
    ProsjektAvfallKlimagassField,
    validateKlimagassberegning,
} from './ProsjektAvfall';
import {
    useSetProsjektAvfallKlimagassberegningMutation,
    type ProsjektAvfall,
} from '../services/rtkApi';

type ProsjektAvfallKlimagassUploadProps = {
    prosesser: Array<ProsjektAvfall>;
};

export function ProsjektAvfallKlimagassUpload({
    prosesser,
}: ProsjektAvfallKlimagassUploadProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [selectedProsess, setSelectedProsess] = useState<ProsjektAvfall>();
    const [klimagassValue, setKlimagassValue] = useState<number | null>(null);
    const [error, setError] = useState<string>();
    const [setKlimagassberegning, { isLoading }] =
        useSetProsjektAvfallKlimagassberegningMutation();

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setOpen(false);
        setSelectedProsess(undefined);
        setKlimagassValue(null);
        setError(undefined);
    };

    const handleSelectProsess = (prosess: ProsjektAvfall | undefined) => {
        setSelectedProsess(prosess);
        setKlimagassValue(prosess?.klimagassberegning ?? null);
        setError(undefined);
    };

    const handleValueChange = (value: number | null) => {
        setKlimagassValue(value);
        setError(undefined);
    };

    const handleConfirm = () => {
        if (!selectedProsess) return;

        const errors = validateKlimagassberegning(klimagassValue);
        if (errors.klimagassberegning) {
            setError(errors.klimagassberegning);
            return;
        }

        setKlimagassberegning({
            prosessInstansId: selectedProsess.prosessInstansId,
            klimagassberegning: klimagassValue,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t(
                        'sustainability.prosjektAvfall.updateKlimagassSuccess',
                    ),
                    type: 'success',
                });
                handleClose();
            })
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    return (
        <Box>
            <Button variant="contained" color="secondary" onClick={handleOpen}>
                {t('sustainability.prosjektAvfall.uploadKlimagass')}
            </Button>
            <KlpModal
                isOpen={open}
                onClose={handleClose}
                translation="sustainability.prosjektAvfall.uploadKlimagass"
            >
                <Block marginTop="fat" marginBottom="compact">
                    <Autocomplete<ProsjektAvfall>
                        id="prosjekt-klimagass-select"
                        selectOnFocus={true}
                        clearOnBlur={true}
                        handleHomeEndKeys={true}
                        options={prosesser}
                        getOptionLabel={(option) => option.prosjektnavn}
                        getOptionSelected={(option, value) =>
                            option.prosessInstansId === value.prosessInstansId
                        }
                        value={selectedProsess ?? null}
                        onChange={(_event, newValue) =>
                            handleSelectProsess(newValue ?? undefined)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth={true}
                                required={true}
                                variant="outlined"
                                label={t(
                                    'sustainability.prosjektAvfall.projectLabel',
                                )}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    />
                </Block>
                <Block marginBottom="fat">
                    <ProsjektAvfallKlimagassField
                        isEditMode={true}
                        layout="stacked"
                        value={klimagassValue}
                        error={error}
                        onChange={handleValueChange}
                    />
                </Block>
                <Button
                    disabled={!selectedProsess || isLoading}
                    variant="contained"
                    color="secondary"
                    onClick={handleConfirm}
                >
                    {t('sustainability.prosjektAvfall.lagreKlimagass')}
                </Button>
            </KlpModal>
        </Box>
    );
}
